<script setup lang="ts">
import { computed, ref } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 600));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      fontFamily: props.block.fontFamily || "'Inter', system-ui, sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      perspective: "1200px",
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.6}px`,
    fontSize: `${base}px`,
    radius: `${props.block.cardRadius ?? 22}px`,
  };
});

const tilt = ref<Record<string, { rx: number; ry: number }>>({});

function onMove(key: string, e: MouseEvent): void {
  if (!props.block.enableHoverFx) return;
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const x = (e.clientX - rect.left) / rect.width - 0.5;
  const y = (e.clientY - rect.top) / rect.height - 0.5;
  tilt.value = { ...tilt.value, [key]: { rx: -y * 18, ry: x * 18 } };
}
function onLeave(key: string): void {
  tilt.value = { ...tilt.value, [key]: { rx: 0, ry: 0 } };
}
</script>

<template>
  <div class="cd-3d" :style="containerStyle">
    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-3d__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 100}ms`,
        transform: `rotateX(${tilt[seg.key]?.rx ?? 0}deg) rotateY(${
          tilt[seg.key]?.ry ?? 0
        }deg)`,
      }"
      @mousemove="onMove(seg.key, $event)"
      @mouseleave="onLeave(seg.key)"
    >
      <div class="cd-3d__shine" />
      <div
        class="cd-3d__digit"
        :style="{
          fontSize: cardSize.fontSize,
          color: block.digitColor || '#1f2937',
        }"
      >
        <span :key="seg.value">{{ seg.value }}</span>
      </div>
      <div
        class="cd-3d__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || '#6b7280',
        }"
      >
        {{ seg.label }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-3d {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
}
.cd-3d__cell {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #ffffff, #f1f5f9);
  box-shadow:
    0 30px 60px -20px rgba(31, 41, 55, 0.4),
    0 18px 36px -18px rgba(31, 41, 55, 0.3),
    0 -2px 6px rgba(255, 255, 255, 0.6) inset,
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
  transform-style: preserve-3d;
  transition:
    transform 0.18s ease-out,
    box-shadow 0.3s ease;
  animation: cd-3d-rise 700ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
  will-change: transform;
  overflow: hidden;
}
@keyframes cd-3d-rise {
  from {
    opacity: 0;
    transform: translateY(28px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.cd-3d__shine {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at 30% 20%,
    rgba(255, 255, 255, 0.7) 0%,
    transparent 55%
  );
  pointer-events: none;
}
.cd-3d__digit {
  position: relative;
  z-index: 1;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  transform: translateZ(40px);
}
.cd-3d__digit span {
  display: inline-block;
  animation: cd-3d-tick var(--cd-tick, 600ms) cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes cd-3d-tick {
  0% {
    opacity: 0;
    transform: translateY(-12px) scale(1.1);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.cd-3d__label {
  position: relative;
  z-index: 1;
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-weight: 500;
  transform: translateZ(20px);
}
</style>
